import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterBar from "@/components/feature/FilterBar";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  applyFilters,
  getCategoryConfig,
  getFilterOptions,
  getMediaByType,
  PAGE_SIZE,
  sortMedia,
  type MediaFilters,
  type SortKey,
} from "@/lib/media";

const emptyFilters: MediaFilters = { genres: [], regions: [], years: [] };

export default function Category() {
  const { type = "" } = useParams();
  const config = getCategoryConfig(type);

  const [filters, setFilters] = useState<MediaFilters>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const baseItems = useMemo(() => (config ? getMediaByType(config.key) : []), [config]);
  const options = useMemo(() => getFilterOptions(baseItems), [baseItems]);

  const filtered = useMemo(() => {
    const result = sortMedia(applyFilters(baseItems, filters), sort);
    return result;
  }, [baseItems, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useDocumentMeta({
    title: config
      ? `${config.name} - 91视频网站影视资源分类`
      : "分类未找到 - 91视频网站",
    description: config
      ? `${config.description}`
      : "抱歉，你访问的影视分类不存在，请返回首页浏览其他分类内容。",
    keywords: config
      ? `${config.name},${config.name}在线观看,91视频网站,${config.name}资源`
      : "91视频网站,影视资源",
    canonicalPath: `/category/${type}`,
  });

  const handleToggle = (dimension: "genres" | "regions" | "years", value: string) => {
    setFilters((prev) => {
      const list = prev[dimension];
      const next = list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value];
      return { ...prev, [dimension]: next };
    });
    setPage(1);
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setSort("latest");
    setPage(1);
  };

  if (!config) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="该影视分类不存在"
          description="你访问的分类页面可能已下线或链接有误，返回首页继续发现好看的影视内容吧。"
          onReset={handleReset}
          resetLabel="重新开始浏览"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-4 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-900">{config.name}</span>
      </nav>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${config.icon} text-2xl`} aria-hidden="true"></i>
          </span>
          <div>
            <h1 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              {config.name}
            </h1>
            <p className="mt-0.5 max-w-2xl text-xs text-foreground-500 md:text-sm">{config.intro}</p>
          </div>
        </div>
        <span className="shrink-0 text-sm text-foreground-500">
          共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
        </span>
      </div>

      <div className="mb-6">
        <FilterBar
          options={options}
          filters={filters}
          onToggle={handleToggle}
          onReset={handleReset}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
        />
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pageItems.map((item) => (
              <MediaCard key={item.id} item={item} showActors />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          title="没有找到符合条件的影视"
          description="试试减少筛选条件，或者重置筛选，浏览该分类下的全部内容。"
          onReset={handleReset}
        />
      )}
    </div>
  );
}